// Auth context for the data-collection module.
//
// Two paths:
//   - Standalone build (`data-dev.dexmate.ai`): the user signs into WorkOS
//     SSO; we land at /auth/callback, persist the JWT in a cookie, and read
//     it from `document.cookie`. Operator tokens (`DEXUPLOAD_TOKEN`) are
//     supported as an override for local-dev.
//   - dexhub-module build: dexhub already wrote the JWT cookie. We just read
//     it. Token refresh is dexhub's responsibility.

const TOKEN_COOKIE = 'dexhub_token';
const TOKEN_LOCAL_KEY = 'DEXUPLOAD_TOKEN';

export type BuildTarget = 'standalone' | 'dexhub-module';

export function buildTarget(): BuildTarget {
  // Vite injects this via define() in vite.config.ts.
  const t = (import.meta.env.VITE_BUILD_TARGET ?? 'standalone') as BuildTarget;
  return t === 'dexhub-module' ? 'dexhub-module' : 'standalone';
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return m && m[1] ? decodeURIComponent(m[1]) : null;
}

export function getAuthToken(): string | null {
  // Local override always wins. Useful for the operator running the SPA on
  // localhost while testing CLI uploads with their `DEXUPLOAD_TOKEN`.
  if (typeof localStorage !== 'undefined') {
    const explicit = localStorage.getItem(TOKEN_LOCAL_KEY);
    if (explicit) return explicit;
  }
  return readCookie(TOKEN_COOKIE);
}

export interface AuthHeaders {
  Authorization?: string;
}

export function authHeaders(): AuthHeaders {
  const tok = getAuthToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

export class AuthError extends Error {
  constructor(message = 'not authenticated') {
    super(message);
    this.name = 'AuthError';
  }
}

export function requireAuthOrRedirect(loginPath = '/auth/login'): string {
  const tok = getAuthToken();
  if (tok) return tok;
  if (typeof window !== 'undefined' && buildTarget() === 'standalone') {
    window.location.assign(loginPath);
  }
  throw new AuthError();
}
